import { type StakingOrigin, StakingType } from '@capsicum/common';
import { useFanTokenStakingConfig, useGetFanTokenStakeOperationByCurrencyIds } from '@capsicum/socios-core';

import { useFanTokenStakingRouter } from './useFanTokenStakingRouter';

/**
 * @param currencyId - string
 * @param origin - string Entry point to fan-token-staking. If provided it will be used to navigate back at the end of the flow
 * @param originIds - Array<string> Optional param if entry point url includes ids
 * @description This hook contains all necessary logic needed to redirect to fan-token-staking.
 * If origin param is provided it will be used to redirect back to the entry point at the end of the staking flow.
 *
 * the originIds contains the needed param for the navigation to the origin IN THE CORRECT ORDER
 *
 * Example:
 * /fan-token-staking/{currencyId}/stake?origin={StakingOrigin.VOTES}&originIds={partnerId}&originIds={surveyId}
 *
 * const options = {origin: StakingOrigin.VOTES, originIds: [partnerId, survayId]}
 **/
export const useFanTokenStakingNavigation = (
	currencyId?: string,
	options?: { origin?: StakingOrigin; originIds?: Array<string> },
	withHiddenNavigation?: boolean
) => {
	const { handleNavigationToStake } = useFanTokenStakingRouter();

	const { areConfigsLoading, isAllowed } = useFanTokenStakingConfig(StakingType.STAKE);
	const { getOperationDetails, isOperationDetailsLoading } = useGetFanTokenStakeOperationByCurrencyIds({
		currencyIds: currencyId && currencyId.length ? [currencyId] : []
	});

	const operationDetails = getOperationDetails(currencyId);

	const navToFanTokenStake = () => {
		if (!operationDetails) return;
		handleNavigationToStake({
			currencyId,
			currentStake: operationDetails?.user.currentStake,
			origin: options?.origin,
			originIds: options?.originIds,
			withHiddenNavigation
		});
	};

	return {
		isLoading: areConfigsLoading || isOperationDetailsLoading,
		isStakeAllowed: isAllowed && operationDetails?.hasCurrencyStakeOperation,
		navToFanTokenStake,
		operationDetails,
		userCurrentStake: operationDetails?.user?.currentStake,
		userHasCurrentStake: operationDetails?.user?.hasCurrentStake
	};
};
